import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ITeeTime } from "redux/reducers/models/teetime";
import "./reviewPlayerDetailsModal.scss";
import { ICustomer } from "redux/reducers/models/customer";
import classNames from "classnames";
import Tabs from "components/tabs/Tabs";
import { Badge } from "components/badge/Badge";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faExclamationCircle, faGift, faHouseBlank, faTicketPerforated } from "@fortawesome/pro-regular-svg-icons";
import { GetGiftCard, IGiftCard } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";
import { useAppDispatch } from "hooks/redux";
import { dequeue, enqueue, showError } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { LocaleCurrency } from "helpers/Locale";
import { GetAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { IAccount } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import ReactDOM from "react-dom";
import { GetTicketStub } from "api/rpc/2024-04/facilityAdmin/ticket/ticket";
import { IStub } from "redux/reducers/models/ticket";
import { ButtonNew as Button } from "components/buttonNew";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { GetCustomerNotes } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetBookingNotes } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";

interface IProps {
    open: boolean;
    customers: Array<ICustomer>;
    onClose: () => void;

    //------Required for tee sheet details page-----
    selectedTeeTime?: ITeeTime;
    redeemTicketStub?: (stub: IStub, customerId: number, participantId: number, bookingId: number) => void;
}

enum SelectedTab {
    Notes = 0,
    Tickets = 1,
    GiftCards = 2,
    HouseAccounts = 3,
}

interface IState {
    [key: number]: {
        notes: Array<Record<string, any>>;
        giftCards: Array<IGiftCard>;
        accounts: Array<IAccount>;
        ticketStubs: Array<IStub>;
    }
}

const TICKET_LIMIT = 50;

export default function ReviewPlayerDetailsModal(props: IProps) {
    const { open, selectedTeeTime, onClose, redeemTicketStub, customers } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [selectedCustomer, setSelectedCustomer] = useState<ICustomer>();
    const [selectedTab, setSelectedTab] = useState<SelectedTab>(SelectedTab.Notes);

    const [state, setState] = useState<IState>({});
    // Per-customer ticket pagination offsets
    const [ticketOffsets, setTicketOffsets] = useState<Record<number, number>>({});

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (customers && customers?.length > 0 && open) {
            const firstCustomer = customers[0];
            setSelectedCustomer(firstCustomer);
            // Reset ticket offsets when modal opens with new customers
            setTicketOffsets({});
            void loadAll(source.token);
        }
        return () => source.cancel();
    }, [customers, open]);

    //Close modal when enter key is pressed
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                onClose();
            }
        };

        if (open) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    //Get notes on each unique booking
    async function getBookingNotes(cancelToken: CancelToken) {
        if (!selectedTeeTime) {
            return null;
        }

        const uniqueBookings = selectedTeeTime.slots
            ?.map(slot => slot.booking)
            ?.filter(Boolean)
            ?.filter((booking, index, array) =>
                array.findIndex(b => b?.id === booking?.id) === index,
            );

        const uniqueBookingIds = uniqueBookings?.map(booking => booking?.id);
        const bookingNotesRes = await Promise.all(uniqueBookingIds?.map(async bookingId => GetBookingNotes({ tee_time_booking_id: bookingId }, false, cancelToken)));
        const allNotes = bookingNotesRes?.filter(res => res?.status === StatusCode.OK)?.map(res => res?.data as Record<string, any>);
        const notes: Array<Record<string, any>> = []?.concat(...allNotes);

        if (notes?.length === 0) {
            return {};
        }

        const updatedState: IState = {};

        // Loop through each non guest player
        selectedTeeTime.slots
            ?.filter(slot => !slot?.booking_participant?.guest)
            ?.forEach(slot => {
                const customer = slot.booking_participant?.customer;
                if (!customer?.id) {
                    return;
                }

                const customerSlot = slot;

                const customerBooking = uniqueBookings?.find(
                    booking => booking?.id === customerSlot?.tee_time_booking_id,
                );

                const filteredSlots = selectedTeeTime.slots?.filter(
                    s => s.booking?.id === customerBooking?.id,
                );

                const findCustomerSlotIndex = filteredSlots?.findIndex(
                    s =>
                        s?.tee_time_booking_participant_id ===
                        customerSlot?.tee_time_booking_participant_id,
                );

                const customerNotesForPlayer =
                    notes.filter(n => n.customer_id === customer.id) ?? [];

                const bookingNotesForPlayer =
                    notes.filter(n => {

                        const hasParticipant = n?.tee_time_booking_participant_id != null;

                        const matchesParticipant = n?.tee_time_booking_participant_id === customerSlot?.booking_participant?.id;

                        const matchesBooking =
                            !hasParticipant &&
                            n?.tee_time_booking_id === customerSlot?.booking?.id &&
                            findCustomerSlotIndex === 0;

                        return matchesParticipant || matchesBooking;

                    }) ?? [];

                const combinedNotes = [
                    ...customerNotesForPlayer,
                    ...bookingNotesForPlayer,
                ];

                updatedState[customer.id] = {
                    notes: combinedNotes,
                    giftCards: [],
                    accounts: [],
                    ticketStubs: [],
                };
            });

        return updatedState;
    }

    async function loadAll(cancelToken: CancelToken) {
        if (!customers) {
            return;
        }
        dispatch(enqueue());

        try {
            const uniqueCustomersMap = new Map<number, ICustomer>();
            customers?.forEach(c => {
                if (c?.id) {
                    uniqueCustomersMap.set(c.id, c);
                }
            });

            const uniqueCustomers = Array.from(uniqueCustomersMap.values());
            let bookingNotes: Partial<IState> = {};
            if (selectedTeeTime) {
                //Get booking notes
                bookingNotes = await getBookingNotes(cancelToken);
            }

            const results = await Promise.all(
                uniqueCustomers.map(async customer => {
                    const [giftCardRes, accountsRes, stubRes, notesRes] = await Promise.all([
                        GetGiftCard({ customer_id: customer.id }, false, cancelToken),
                        GetAccount({ customer_id: customer.id }, false, cancelToken),
                        GetTicketStub({ customer_id: customer.id, status: "valid", shared: true, extended: true, redemption_products: true, limit: TICKET_LIMIT, offset: 0 }, false, cancelToken),
                        GetCustomerNotes({ customer_id: customer.id }, false, cancelToken),
                    ]);

                    return {
                        customerId: customer.id,
                        giftCardRes,
                        accountsRes,
                        stubRes,
                        notesRes,
                    };
                }),
            );

            const nextState: IState = {};

            results.forEach(({ customerId, giftCardRes, accountsRes, stubRes, notesRes }) => {
                let giftCards: IGiftCard[] = [];
                let accounts: IAccount[] = [];
                let ticketStubs: IStub[] = [];
                let customerNotes: Record<string, any>[] = [];

                if (giftCardRes?.status === StatusCode.OK) {
                    giftCards =
                        giftCardRes.data
                            ?.filter(card => card?.status === "valid" && card?.balance > 0)
                            ?.map(card => card) ?? [];
                } else {
                    if (cancelToken?.reason) {
                        return;
                    }
                    dispatch(showError(t("Error getting gift cards")));
                }

                if (accountsRes?.status === StatusCode.OK) {
                    accounts = (accountsRes.data as Array<IAccount>) ?? [];
                } else {
                    if (cancelToken?.reason) {
                        return;
                    }
                    dispatch(showError(t("Error getting accounts")));
                }

                if (stubRes?.status === StatusCode.OK) {
                    ticketStubs = (stubRes.data as Array<IStub>) ?? [];
                } else {
                    if (cancelToken?.reason) {
                        return;
                    }
                    dispatch(showError(t("Error getting tickets")));
                }

                if (notesRes?.status === StatusCode.OK) {
                    customerNotes = (notesRes.data as Array<Record<string, any>>) ?? [];
                    customerNotes = bookingNotes[customerId]?.notes ? [...customerNotes, ...bookingNotes[customerId].notes] : customerNotes;
                    customerNotes?.sort((a, b) => {
                        const getRank = (n: any) => {
                            const isHigh = n?.high_priority === true;
                            const hasBooking = n?.tee_time_booking_id != null;

                            if (isHigh) {
                                return 3;
                            } //High priority first
                            if (hasBooking) {
                                return 2;
                            } //Booking notes second
                            return 1; //Customer notes last
                        };
                        return getRank(b) - getRank(a);
                    });
                } else {
                    if (cancelToken?.reason) {
                        return;
                    }
                    dispatch(showError(t("Error getting notes")));
                }

                nextState[customerId] = {
                    notes: customerNotes,
                    giftCards,
                    accounts,
                    ticketStubs,
                };
            });

            ReactDOM.unstable_batchedUpdates(() => {
                setState(prev => {
                    const next = { ...prev };
                    Object.entries(nextState).forEach(([customerId, value]) => {
                        const id = Number(customerId);
                        next[id] = {
                            ...prev[id],
                            giftCards: value.giftCards,
                            accounts: value.accounts,
                            ticketStubs: value.ticketStubs,
                            notes: value.notes,
                        };
                    });

                    return next;
                });
                setSelectedTab(SelectedTab.Notes);
            });


        } finally {
            dispatch(dequeue());
        }
    }

    function selectPlayer(customer: ICustomer) {
        setSelectedCustomer(customer);
    }

    async function loadTicketStubsForCustomer(customerId: number, offset: number) {
        dispatch(enqueue());
        try {
            const stubRes = await GetTicketStub(
                { customer_id: customerId, status: "valid", shared: true, extended: true, redemption_products: true, limit: TICKET_LIMIT, offset },
                false,
            );
            if (stubRes?.status === StatusCode.OK) {
                const ticketStubs = (stubRes.data as Array<IStub>) ?? [];
                setState(prev => ({
                    ...prev,
                    [customerId]: {
                        ...prev[customerId],
                        ticketStubs,
                    },
                }));
                setTicketOffsets(prev => ({ ...prev, [customerId]: offset }));
            } else {
                dispatch(showError(t("Error getting tickets")));
            }
        } finally {
            dispatch(dequeue());
        }
    }

    function handleTicketPagination(direction: "prev" | "next") {
        const customerId = selectedCustomer?.id;
        if (!customerId) {
            return;
        }
        const currentOffset = ticketOffsets[customerId] ?? 0;
        const newOffset = direction === "next" ? currentOffset + TICKET_LIMIT : currentOffset - TICKET_LIMIT;
        void loadTicketStubsForCustomer(customerId, newOffset);
    }

    function redeemTicket(stub: IStub, customerId: number) {
        const foundSlot = selectedTeeTime?.slots?.find(slot => slot?.booking_participant?.customer?.id === customerId);
        if (foundSlot) {
            const participantId = foundSlot?.tee_time_booking_participant_id;
            const bookingId = foundSlot?.tee_time_booking_id;
            void redeemTicketStub(stub, customerId, participantId, bookingId);
        }
    }

    return (
        <Portal isMounted={open}>
            <Sheet
                open={open}
                title={t("Review Player Details")}
                size="medium"
                closable
                okText={t("Close")}
                hideCancelButton
                onOk={onClose}
                onCancel={onClose}
                fullWidthOK
                supportText={t("Review notes and balances for players in this tee time")}
            >
                <div className="review-players-modal-container">
                    <div className="review-players-modal-players-container">
                        {customers?.map((customer, index) => {
                            return (
                                <div key={index} className={classNames("review-players-modal-players-card", { "review-players-modal-players-card-selected": customer?.id === selectedCustomer?.id })} onClick={() => selectPlayer(customer)}>
                                    <div className={classNames("review-players-modal-players-card-avatar", { "review-players-modal-players-card-avatar-selected": customer?.id === selectedCustomer?.id })}>
                                        {customer?.first_name?.charAt(0)}{customer?.last_name?.charAt(0)}
                                        {state[customer?.id]?.notes?.length > 0 || state[customer?.id]?.giftCards?.length > 0 || state[customer?.id]?.accounts?.length > 0 || state[customer?.id]?.ticketStubs?.length > 0 ? (
                                            <div className="review-players-modal-players-card-avatar-notification"></div>
                                        ) : null}
                                    </div>
                                    <div>
                                        <p className="review-players-modal-players-card-primary">{customer?.full_name}</p>
                                        <p className="review-players-modal-players-card-secondary">{customer?.customer_type}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {state[selectedCustomer?.id]?.notes?.length === 0 && state[selectedCustomer?.id]?.giftCards?.length === 0 && state[selectedCustomer?.id]?.accounts?.length === 0 && state[selectedCustomer?.id]?.ticketStubs?.length === 0 ? (
                        <div style={{ marginTop: "5rem" }}>
                            <MessageDisplay icon="thumbs-up" title={t("Nothing to view for {{playerName}}", { playerName: selectedCustomer?.full_name })} content={t("This player has no active tickets, gift cards, notes or house accounts on file")} />
                        </div>
                    ) : (
                        <div className="review-players-modal-details-container">
                            <Tabs tabs={[{
                                content: (
                                    <span className="flex items-center gap-1">
                                        {t("Notes")}
                                        {state[selectedCustomer?.id]?.notes?.length > 0 && (
                                            <div className={classNames("review-players-modal-notification-badge", { "review-players-modal-notification-badge-selected": selectedTab === SelectedTab.Notes })}>
                                                {state[selectedCustomer?.id]?.notes?.length}
                                            </div>
                                        )}
                                    </span>
                                ),
                            }, {
                                content: (
                                    <span className="flex items-center gap-1">
                                        {t("Tickets")}
                                        {state[selectedCustomer?.id]?.ticketStubs?.length > 0 && (
                                            <div className={classNames("review-players-modal-notification-badge", { "review-players-modal-notification-badge-selected": selectedTab === SelectedTab.Tickets })}>
                                                {state[selectedCustomer?.id]?.ticketStubs?.length}
                                            </div>
                                        )}
                                    </span>
                                ),
                            }, {
                                content: (
                                    <span className="flex items-center gap-1">
                                        {t("Gift Cards")}
                                        {state[selectedCustomer?.id]?.giftCards?.length > 0 && (
                                            <div className={classNames("review-players-modal-notification-badge", { "review-players-modal-notification-badge-selected": selectedTab === SelectedTab.GiftCards })}>
                                                {state[selectedCustomer?.id]?.giftCards?.length}
                                            </div>
                                        )}
                                    </span>
                                ),
                            }, {
                                content: (
                                    <span className="flex items-center gap-1">
                                        {t("House Accounts")}
                                        {state[selectedCustomer?.id]?.accounts?.length > 0 && (
                                            <div className={classNames("review-players-modal-notification-badge", { "review-players-modal-notification-badge-selected": selectedTab === SelectedTab.HouseAccounts })}>
                                                {state[selectedCustomer?.id]?.accounts?.length}
                                            </div>
                                        )}
                                    </span>
                                ),
                            }]} selected={selectedTab} variant="underline" onSelect={tab => setSelectedTab(tab)} />
                            {selectedTab === SelectedTab.Notes ? (
                                <div className="review-players-modal-details-container">
                                    {state[selectedCustomer?.id]?.notes?.map((note, index) => {
                                        return (
                                            <div key={index} className="review-players-modal-note-card">
                                                {note.high_priority && <Badge iconLeft={<FontAwesomeIcon size="1x" icon={faExclamationCircle} style={{ color: '#dc2626' }} />} size="small" type="error">{t("High")}</Badge>}
                                                <div className="review-players-modal-note-card-content">{note.content}</div>
                                                <div className="note-header">
                                                    <span className="review-players-modal-note-card-date">
                                                        {t("Posted on")} {moment(note?.created_at as string).format("LLL")} {t("by")} {note?.author?.full_name}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}

                            {selectedTab === SelectedTab.Tickets ? (
                                <>
                                    <div className="review-players-modal-details-container">
                                        {state[selectedCustomer?.id]?.ticketStubs?.map((stub: IStub & { is_shared?: boolean; redemptions_remaining?: number }, index) => {
                                             const stubRedemptionPending = selectedTeeTime?.slots?.some(slot => slot?.booking_participant?.pending_ticket_stub_ids?.some(findStub => findStub === stub?.id));
                                            return (
                                                <div className="review-players-modal-card" key={index}>
                                                    <div className="flex items-center gap-4">
                                                        <div className="review-players-modal-players-card-avatar">
                                                            <FontAwesomeIcon icon={faTicketPerforated} />
                                                        </div>
                                                        <div>
                                                            <p className="review-players-modal-note-card-content">{stub?.ticket?.title} {stub?.ticket?.subtitle ? ` - ${stub?.ticket?.subtitle}` : null} {stub?.is_shared ? <Badge type="primary" size="xsmall">{t("Shared")}</Badge> : null} {stubRedemptionPending ? <Badge type="warning" size="xsmall">{t("Pending")}</Badge> : null}</p>
                                                            <p className="review-players-modal-note-card-date">{stub.ticket?.included}</p>
                                                            <p className="review-players-modal-note-card-date">{stub.code}</p>
                                                            <p className="review-players-modal-note-card-date">{stub.expiry_date ? `${t("Expires: ")} ${moment(stub.expiry_date, "YYYY-MM-DD")?.format("LL")}` : null}</p>
                                                            {stub?.ticket?.multi_use ? <p className="review-players-modal-note-card-date">{t("Redemptions Remaining: ")} {stub?.redemptions_remaining}</p> : null}
                                                        </div>
                                                    </div>
                                                    {redeemTicketStub ? (
                                                        <Button disabled={stubRedemptionPending} size="small" type="text" onClick={() => redeemTicket(stub, selectedCustomer?.id)}>{t("Redeem")}</Button>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Pagination buttons — only shown when there are more than 50 tickets */}
                                    {(() => {
                                        const customerId = selectedCustomer?.id;
                                        const currentOffset = ticketOffsets[customerId] ?? 0;
                                        const stubCount = state[customerId]?.ticketStubs?.length ?? 0;
                                        const hasPrev = currentOffset > 0;
                                        const hasNext = stubCount === TICKET_LIMIT;
                                        if (!hasPrev && !hasNext) {
                                            return null;
                                        }
                                        return (
                                            <div className="review-players-modal-ticket-pagination">
                                                <Button
                                                    size="small"
                                                    type="secondary"
                                                    disabled={!hasPrev}
                                                    onClick={() => handleTicketPagination("prev")}
                                                >
                                                    <FontAwesomeIcon icon={faArrowLeft} />&nbsp;{t("Previous")}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    type="secondary"
                                                    disabled={!hasNext}
                                                    onClick={() => handleTicketPagination("next")}
                                                >
                                                    {t("Next")}&nbsp;<FontAwesomeIcon icon={faArrowRight} />
                                                </Button>
                                            </div>
                                        );
                                    })()}
                                </>
                            ) : null}

                            {selectedTab === SelectedTab.GiftCards ? (
                                <div className="review-players-modal-details-container">
                                    {state[selectedCustomer?.id]?.giftCards?.map((giftCard, index) => {
                                        return (
                                            <div className="review-players-modal-card" key={index}>
                                                <div className="flex items-center gap-4">
                                                    <div className="review-players-modal-players-card-avatar">
                                                        <FontAwesomeIcon icon={faGift} />
                                                    </div>
                                                    <div>
                                                        <p className="review-players-modal-note-card-content">••••••••{giftCard?.last4}</p>
                                                        <p className="review-players-modal-note-card-date">{t("Balance: ")} <LocaleCurrency amount={giftCard?.balance} currency={giftCard?.currency ?? "CAD"} /></p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}

                            {selectedTab === SelectedTab.HouseAccounts ? (
                                <div className="review-players-modal-details-container">
                                    {state[selectedCustomer?.id]?.accounts?.map((account, index) => {
                                        return (
                                            <div className="review-players-modal-card" key={index}>
                                                <div className="flex items-center gap-4">
                                                    <div className="review-players-modal-players-card-avatar">
                                                        <FontAwesomeIcon icon={faHouseBlank} />
                                                    </div>
                                                    <div>
                                                        <p className="review-players-modal-note-card-content">{account?.title}</p>
                                                        <p className="review-players-modal-note-card-date">{t("Balance: ")} <LocaleCurrency amount={account?.balance} currency={account?.currency ?? "CAD"} /></p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}

                        </div>
                    )}

                </div>

            </Sheet>
        </Portal>
    );
}